import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faArrowLeft,
    faEnvelope,
    faSignOutAlt,
    faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { useSafeUserData, useUserDataStore } from '../../../../stateStores/User'
import { formatPersonName } from '../../../../utils/personUtils'
import { AuthHelper } from '../../../../auth/AuthHelper'
import { UserDto, UserRoles } from '../../../../api/tenant/models/UserDto'
import tenantApi from '../../../../api/tenant/TenantApi'
import DropDown, { Item } from '../../form/inputs/DropDown'
import SupportEmailLink from '../../links/SupportEmailLink'
import Section from '../../sections/Section'
import UserRolePill from '@/components/UserRolePill'
import { useLocation, useNavigate } from 'react-router-dom'
import { Pages } from '../../../../Pages'
import { getUserProfilePicUrl } from '../../../../utils/userUtils'
import ToggleSwitch3 from '../../../../components/ToggleSwitch3'

function UserMenu(): React.JSX.Element {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const { user, tenant } = useSafeUserData()
    const setUserData = useUserDataStore((state) => state.setUserData)
    const navigate = useNavigate()
    const location = useLocation()
    const [tenantDropDownItems, setTenantDropDownItems] = useState<Item[]>([])
    const [selectTenantId, setSelectTenantId] = useState<string | null>(null)
    const [displayRoles, setDisplayRoles] = useState<UserRoles[]>([])
    const [showJobSeekers, setShowJobSeekers] = useState(
        tenant?.isPersonal === true,
    )
    const [tenantNameMap, setTenantNameMap] = useState<Map<string, string>>(
        new Map(),
    )
    const updateDisplayedRoles = React.useCallback(
        (tenantId: string | null, memberships: UserDto['memberships']) => {
            if (!tenantId) {
                setDisplayRoles([])
                return
            }

            const membershipForTenant = memberships?.find(
                (membership) => membership.tenantId === tenantId,
            )

            setDisplayRoles(membershipForTenant?.roles ?? [])
        },
        [],
    )

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen)
    }

    // Fetch tenants (re-runs when showJobSeekers toggle changes)
    useEffect(() => {
        async function fetchTenants() {
            let items: Item[] = []
            const nameMap = new Map<string, string>()

            if (user.isProductAdmin) {
                try {
                    const tenants = (
                        await tenantApi.getAll({
                            includePersonal: showJobSeekers,
                        })
                    ).tenants
                    tenants.forEach((t) => nameMap.set(t.id, t.name))
                    items = tenants.map((t) => ({
                        id: t.id,
                        content: (
                            <span className="flex items-center gap-2">
                                {t.name}
                                {t.isPersonal && (
                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                        Job Seeker
                                    </span>
                                )}
                            </span>
                        ),
                    }))
                } catch (error) {
                    console.error('Failed to load tenants for user menu', error)
                }
            }

            if (!user.isProductAdmin || items.length === 0) {
                const memberships = user.memberships ?? []

                const membershipItems = memberships
                    .filter((membership) => membership.tenantId)
                    .map((membership) => ({
                        id: membership.tenantId,
                        content: membership.tenant?.name ?? membership.tenantId,
                    }))

                if (membershipItems.length > 0) {
                    const seen = new Set(items.map((item) => item.id))
                    for (const membershipItem of membershipItems) {
                        if (!seen.has(membershipItem.id)) {
                            items.push(membershipItem)
                            seen.add(membershipItem.id)
                        }
                    }
                }

                memberships.forEach((m) => {
                    if (m.tenantId) {
                        nameMap.set(m.tenantId, m.tenant?.name ?? m.tenantId)
                    }
                })
            }

            // Remove duplicates while preserving order
            const uniqueItems: Item[] = []
            const seenIds = new Set<string>()
            for (const item of items) {
                if (!seenIds.has(item.id)) {
                    uniqueItems.push(item)
                    seenIds.add(item.id)
                }
            }

            setTenantDropDownItems(uniqueItems)
            setTenantNameMap(nameMap)

            const candidateTenantId =
                AuthHelper.getTenantOverrideId() ??
                user.activeTenantId ??
                uniqueItems[0]?.id ??
                null

            // Check if the candidate tenant is still in the visible list
            const candidateInList =
                candidateTenantId != null &&
                uniqueItems.some((item) => item.id === candidateTenantId)

            const resolvedTenantId = candidateInList
                ? candidateTenantId
                : (uniqueItems[0]?.id ?? null)

            // If the candidate was stale (not in the filtered list), update the cookie
            if (!candidateInList && candidateTenantId != null) {
                if (
                    resolvedTenantId != null &&
                    resolvedTenantId !== user.activeTenantId
                ) {
                    AuthHelper.setTenantOverrideId(resolvedTenantId)
                } else {
                    AuthHelper.removeTenantOverrideId()
                }
            }

            if (resolvedTenantId) {
                setSelectTenantId(resolvedTenantId)
                updateDisplayedRoles(resolvedTenantId, user.memberships)
            } else {
                setSelectTenantId(null)
                setDisplayRoles([])
            }

            if (
                user.isProductAdmin &&
                !AuthHelper.getTenantOverrideId() &&
                resolvedTenantId
            ) {
                AuthHelper.setTenantOverrideId(resolvedTenantId)
            }
        }

        fetchTenants()
    }, [user, tenant.id, updateDisplayedRoles, showJobSeekers])

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    async function handleTenantChanged(item: Item) {
        const tenantId = item.id
        // set cookie
        const activeTenantId = user.activeTenantId ?? null
        if (tenantId === activeTenantId) {
            // they have selected their real tenant, so we can remove the override
            AuthHelper.removeTenantOverrideId()
        } else {
            AuthHelper.setTenantOverrideId(tenantId)
        }
        setSelectTenantId(tenantId)

        const membershipForTenant = user.memberships?.find(
            (membership) => membership.tenantId === tenantId,
        )

        if (membershipForTenant) {
            const resolvedTenantName =
                membershipForTenant.tenant?.name ??
                tenantNameMap.get(tenantId) ??
                tenant?.name ??
                tenantId

            const tenantForMembership =
                membershipForTenant.tenant &&
                membershipForTenant.tenant.id === tenant?.id
                    ? tenant
                    : membershipForTenant.tenant

            setUserData({
                user: {
                    ...user,
                    activeTenantId: tenantId,
                },
                tenant: tenantForMembership ?? {
                    id: tenantId,
                    name: resolvedTenantName,
                    isPersonal: false,
                    companies: tenant?.companies ?? [],
                    availableAsSeedPersonIds:
                        tenant?.availableAsSeedPersonIds ?? [],
                },
            })
            updateDisplayedRoles(tenantId, user.memberships)
        } else {
            setUserData({
                user: {
                    ...user,
                    activeTenantId: tenantId,
                },
                tenant: tenant ?? {
                    id: tenantId,
                    name: tenantNameMap.get(tenantId) ?? tenantId,
                    isPersonal: false,
                    companies: [],
                    availableAsSeedPersonIds: [],
                },
            })
            updateDisplayedRoles(tenantId, user.memberships)
        }

        try {
            const res = await tenantApi.getLoggedInUser()
            setUserData({ user: res.user, tenant: res.tenant })
            updateDisplayedRoles(
                res.user.activeTenantId ?? tenantId,
                res.user.memberships,
            )
        } catch (error) {
            console.error('Failed to refresh user after tenant change', error)
        }
    }

    useEffect(() => {
        const effectiveTenantId =
            selectTenantId ?? tenant?.id ?? user.activeTenantId ?? null

        updateDisplayedRoles(effectiveTenantId, user.memberships)
    }, [selectTenantId, user, tenant?.id, updateDisplayedRoles])

    const isInLlmUi = /^\/llm(?:\/|$)/.test(location.pathname)

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Profile Image */}
            <img
                src={getUserProfilePicUrl(user)}
                alt="Profile"
                className="rounded-full w-10 h-10 cursor-pointer border border-gray-300"
                onClick={toggleDropdown}
            />

            {isDropdownOpen && (
                <div
                    className="absolute mt-2 w-56 min-w-fit bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                    style={{ right: '10px' }} // Adjusts the dropdown's position to move it slightly left
                >
                    <div className="px-4 pt-3 border-b border-gray-200">
                        <div className="gap-4">
                            <p className="text-lg font-semibold text-gray-900 text-nowrap">
                                {user.person
                                    ? formatPersonName(user.person)
                                    : user.email}
                            </p>

                            {user.person && (
                                <p className="text-sm text-gray-400">
                                    {user.email}
                                </p>
                            )}

                            {user.isProductAdmin && (
                                <div className="flex items-center justify-between px-1 py-1">
                                    <span className="text-sm text-gray-600">
                                        Show Job Seekers
                                    </span>
                                    <ToggleSwitch3
                                        value={showJobSeekers}
                                        onChange={setShowJobSeekers}
                                    />
                                </div>
                            )}

                            {tenantDropDownItems.length > 1 &&
                            selectTenantId ? (
                                <DropDown
                                    name="Tenant"
                                    id="tenant-select"
                                    items={tenantDropDownItems}
                                    onChange={handleTenantChanged}
                                    selected={
                                        tenantDropDownItems.find(
                                            (item) =>
                                                item.id === selectTenantId,
                                        ) ?? tenantDropDownItems[0]
                                    }
                                    placeholder="Select Tenant"
                                />
                            ) : (
                                <p className="text-md text-gray-600">
                                    {selectTenantId
                                        ? (tenantDropDownItems.find(
                                              (item) =>
                                                  item.id === selectTenantId,
                                          )?.content ??
                                          tenant?.name ??
                                          '')
                                        : tenantDropDownItems.length === 1
                                          ? tenantDropDownItems[0]?.content
                                          : (tenant?.name ?? '')}
                                </p>
                            )}
                        </div>
                        <Section title="" className="gap-4 mt-2 ml-0">
                            <div className="flex flex-wrap gap-1">
                                {(() => {
                                    const roles = new Set<UserRoles>(
                                        displayRoles,
                                    )

                                    if (user.isProductAdmin) {
                                        roles.add(UserRoles.TA)
                                        roles.add(UserRoles.HM)
                                    }

                                    const pills: React.ReactNode[] = []

                                    if (user.isProductAdmin) {
                                        pills.push(
                                            <UserRolePill
                                                key="user-role-product-admin"
                                                isProductAdmin
                                            />,
                                        )
                                    }

                                    if (roles.size === 0) {
                                        pills.push(
                                            <span
                                                key="user-role-none"
                                                className="text-sm text-gray-500"
                                            >
                                                No roles for this tenant
                                            </span>,
                                        )
                                    } else {
                                        Array.from(roles).forEach((userRole) =>
                                            pills.push(
                                                <UserRolePill
                                                    key={`user-role-${userRole}`}
                                                    value={userRole}
                                                />,
                                            ),
                                        )
                                    }

                                    return pills
                                })()}
                            </div>
                        </Section>
                    </div>
                    {user.isProductAdmin && (
                        <button
                            className="flex items-center w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                            onClick={() => {
                                setIsDropdownOpen(false)
                                if (isInLlmUi) {
                                    navigate(Pages.Roles)
                                    return
                                }
                                window.location.assign(Pages.LlmNewRole)
                            }}
                        >
                            <FontAwesomeIcon
                                icon={
                                    isInLlmUi
                                        ? faArrowLeft
                                        : faWandMagicSparkles
                                }
                                className="mr-2"
                            />
                            {isInLlmUi ? 'Back to Regular UI' : 'New LLM UI'}
                        </button>
                    )}
                    <SupportEmailLink className="flex items-center w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 hover:no-underline">
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                        Support
                    </SupportEmailLink>
                    <button
                        className="flex items-center w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                        onClick={() => {
                            // Clear tenant override on logout to prevent it from affecting the next user
                            AuthHelper.removeTenantOverrideId()
                            navigate(Pages.Logout)
                        }}
                    >
                        <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                        Log Out
                    </button>
                </div>
            )}
        </div>
    )
}

export default UserMenu
